// Implements: F-UIX-018, F-USG-005, F-USG-006, F-UIX-010
/**
 * "Consumo" (UIX.5, UX-10): the consumption and per-mockup cost of an organization the person
 * administers or a team they lead, read-only.
 *
 * `ConsumptionByScope` offers the targets the session grants (`reportTargetsOf`) and mounts
 * one `ScopeReportsContainer` keyed by the chosen target (checklist 14): switching scope mounts
 * a fresh one, so no grouping, cached answer or late reply of the previous organization or
 * team reaches the next one's screen (the reads are keyed by the target's ids as well).
 *
 * The screen leads with the key numbers (`ConsumptionTiles`, for one currency at a time: money is
 * never added across currencies), then the daily chart (`DailySpendChart`, always by day, with its
 * table), the rows by the grouping chosen (day, team or person, each with its share of the total),
 * the per-mockup costs and the scope's exports (`ScopeExportsContainer`, 7.S13.9). An organization adds
 * its month's activity and its latency, each with its own "Exportar CSV" (owner 2026-10-19, the admin
 * module's `parts` the composition root hands in), and its cost by model and flow. A team lead gets the
 * same read-only view of the team and its members: nothing on this screen writes (the distribution
 * editor is a separate section). The server decides every read; a refusal is shown as an error state
 * with its retry, never as somebody else's figures. Each section has its own loading, error and empty
 * state (F-UIX-010). No browser storage.
 */

import type { UseQueryResult } from "@tanstack/react-query";
import { useId, useMemo, useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { useTranslation } from "../../../shared/i18n";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { TableSkeleton } from "../../../shared/ui/molecules/TableSkeleton";
import type { BackofficeScope } from "../../../types/api";
import type { ConsumptionGrouping, OrganizationConsumptionView } from "../../../types/reports";
import { ConsumptionTiles } from "../components/ConsumptionTiles";
import { DailySpendChart } from "../components/DailySpendChart";
import { MyMockupCosts } from "../components/MyMockupCosts";
import styles from "../components/MyUsage.module.css";
import { ScopeConsumption } from "../components/ScopeConsumption";
import { useScopeReports } from "../hooks/useScopeReports";
import { ActivityTilesContainer } from "./ActivityTilesContainer";
import { CostBreakdownContainer } from "./CostBreakdownContainer";
import { OrgLatencyContainer } from "./OrgLatencyContainer";
import type { ReportParts } from "./reportParts";
import { ScopeExportsContainer } from "./ScopeExportsContainer";
import { calendarDayText } from "../model/myUsage";
import { groupingsOf, reportTargetsOf, targetKey } from "../model/scopeReports";
import type { ReportTarget } from "../model/scopeReports";
import toolbarStyles from "./ScopeReportsContainer.module.css";

type Translate = ReturnType<typeof useTranslation>["t"];

/** How a target reads in the picker and as its heading. */
function targetLabel(target: ReportTarget, t: Translate): string {
  if (target.kind === "organization") {
    return target.name === null
      ? t("backoffice.consumption.target.unnamedOrganization")
      : t("backoffice.consumption.target.organization", { name: target.name });
  }
  return target.name === null
    ? t("backoffice.consumption.target.unnamedTeam")
    : t("backoffice.consumption.target.team", { name: target.name });
}

/**
 * One section's read: the loading state, the error state with its retry, or `render`. Inside a
 * target's view, so a failure's heading sits under the target's `<h2>`.
 */
function Section<T>({
  read,
  render,
}: {
  readonly read: UseQueryResult<T, ApiError>;
  readonly render: (data: T) => ReactElement;
}): ReactElement {
  if (read.isError) {
    return (
      <ErrorState
        headingLevel={3}
        error={asApiError(read.error)}
        onRetry={() => {
          void read.refetch();
        }}
      />
    );
  }
  if (read.data === undefined) return <TableSkeleton />;
  return render(read.data);
}

export interface ScopeReportsContainerProps {
  readonly target: ReportTarget;
  /** The day the month's sections (activity, costs, latency) end on (checklist 16). */
  readonly today: Date;
  /** The parts another module draws (the latency table, "Exportar CSV"). */
  readonly parts: ReportParts;
}

/** The days of one currency, oldest first, as the chart draws them. */
function dailyPoints(daily: OrganizationConsumptionView, currency: string): { day: string; costMicros: number }[] {
  return daily.rows
    .filter((row) => row.day !== null && row.currency === currency)
    .map((row) => ({ day: row.day ?? "", costMicros: row.cost_micros }))
    .sort((left, right) => (left.day < right.day ? -1 : left.day > right.day ? 1 : 0));
}

/** One organization's or team's reports. Mount it keyed by `targetKey(target)`. */
export function ScopeReportsContainer({ target, today, parts }: ScopeReportsContainerProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const currencyId = useId();
  const groupings = groupingsOf(target);
  const [grouping, setGrouping] = useState<ConsumptionGrouping>(groupings[0] ?? "person");
  const [currencyChoice, setCurrencyChoice] = useState<string | null>(null);
  const { consumption, daily, mockups, teams } = useScopeReports(target, grouping);
  const teamNames = useMemo(
    () => new Map((teams.data?.teams ?? []).map((team) => [team.id, team.name] as const)),
    [teams.data],
  );
  const currencies = consumption.data?.totals.map((total) => total.currency) ?? [];
  const currency = currencies.find((code) => code === currencyChoice) ?? currencies[0];

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <h2 id={headingId} className={styles.sectionTitle}>
        {targetLabel(target, t)}
      </h2>
      {currencies.length > 1 ? (
        <div className={toolbarStyles.toolbar}>
          <label className={toolbarStyles.field} htmlFor={currencyId}>
            {t("backoffice.consumption.currency.label")}
            <select
              id={currencyId}
              className={toolbarStyles.select}
              value={currency ?? ""}
              onChange={(event) => {
                setCurrencyChoice(event.target.value);
              }}
            >
              {currencies.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
      <Section
        read={consumption}
        render={(data) => {
          const figures = data.totals.find((total) => total.currency === currency);
          if (figures === undefined) {
            return <EmptyState icon="chart" title={t("backoffice.consumption.empty.title")} body={t("backoffice.consumption.empty")} />;
          }
          return (
            <>
              <ConsumptionTiles
                figures={figures}
                range={t("backoffice.myUsage.period.range", { from: calendarDayText(data.from), to: calendarDayText(data.to) })}
              />
              {daily.data === undefined ? (
                daily.isError ? (
                  <ErrorState
                    headingLevel={3}
                    error={asApiError(daily.error)}
                    onRetry={() => {
                      void daily.refetch();
                    }}
                  />
                ) : (
                  <TableSkeleton />
                )
              ) : (
                <DailySpendChart key={figures.currency} days={dailyPoints(daily.data, figures.currency)} currency={figures.currency} />
              )}
            </>
          );
        }}
      />
      {target.kind === "organization" ? (
        // Mockup v3 P9: the organization's own reports (org admin only); a team lead asks none of
        // them. Outside the consumption's read, so regrouping the detail never remounts them.
        <>
          <ActivityTilesContainer organizationId={target.organizationId} today={today} parts={parts} />
          <CostBreakdownContainer organizationId={target.organizationId} today={today} />
          <OrgLatencyContainer organizationId={target.organizationId} today={today} parts={parts} />
        </>
      ) : null}
      {consumption.data !== undefined && currency !== undefined ? (
        // The detail, with "Agrupar por" in its own head (owner decision); its failure is the
        // consumption's error state above.
        <ScopeConsumption
          consumption={consumption.data}
          grouping={grouping}
          teamNames={teamNames}
          groupings={groupings}
          onGrouping={setGrouping}
        />
      ) : null}
      <Section
        read={mockups}
        render={(data) => (
          <MyMockupCosts
            headingLevel={3}
            mockups={data}
            emptyBody={t("backoffice.consumption.mockups.emptyBody")}
          />
        )}
      />
      <ScopeExportsContainer target={target} grouping={grouping} />
    </section>
  );
}

export interface ConsumptionByScopeProps {
  /** The session's scopes: what the person administers or leads. */
  readonly scopes: readonly BackofficeScope[];
  /** The day the month's sections end on; today by default (checklist 16). */
  readonly today?: Date | undefined;
  /** The parts another module draws, wired by the composition root. */
  readonly parts: ReportParts;
}

export function ConsumptionByScope({ scopes, today, parts }: ConsumptionByScopeProps): ReactElement {
  const { t } = useTranslation();
  const pickerId = useId();
  const targets = useMemo(() => reportTargetsOf(scopes), [scopes]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [day] = useState<Date>(() => today ?? new Date());
  const target = targets.find((candidate) => targetKey(candidate) === chosen) ?? targets[0];

  if (target === undefined) {
    return <EmptyState title={t("backoffice.consumption.none.title")} body={t("backoffice.consumption.none.body")} />;
  }
  const key = targetKey(target);

  return (
    <div className={styles.section}>
      {targets.length > 1 ? (
        <div className={toolbarStyles.toolbar}>
          <label className={toolbarStyles.field} htmlFor={pickerId}>
            {t("backoffice.consumption.target.label")}
            <select
              id={pickerId}
              className={toolbarStyles.select}
              value={key}
              onChange={(event) => {
                setChosen(event.target.value);
              }}
            >
              {targets.map((candidate) => (
                <option key={targetKey(candidate)} value={targetKey(candidate)}>
                  {targetLabel(candidate, t)}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
      <ScopeReportsContainer key={key} target={target} today={day} parts={parts} />
    </div>
  );
}
